import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, Send, Check, MessageSquare, ChevronRight, Download, Calendar } from 'lucide-react';

import { DDSeg } from './DigestSummary';
import { fetchDigestHistory } from '../../../redux/DigestSlice/digestSlice';
import { DD_TONE, parseDigestDate, toLocalDateKey } from './digestHelpers';

/**
 * Every run the agent has prepared, and what came of it.
 *
 * WHAT THE API ACTUALLY RETURNS. `/digest/history` is deliberately summarised —
 * per row it gives counts, a bySource tally, a byStatus tally and a few sample
 * company names. It does NOT return individual actions. The design comp showed
 * an expanded row naming specific people and reply times ("Todd Schonhardt —
 * replied in 6h"); there is no endpoint behind that, so the expansion renders
 * the real status breakdown instead of inventing a timeline.
 */

const SPARK_DAYS = 30;

const ACTED_STATUSES = ['approved', 'skipped', 'sent', 'manual_sent', 'failed'];

const wasActedOn = (row) => ACTED_STATUSES.some((s) => (row.byStatus || {})[s]);

const sentCount = (row) => (row.counts?.sent) || 0;

const fmtDay = (date) => date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

const relativeLabel = (date, today) => {
    const days = Math.round((today - date) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return null;
};

/** Last 30 calendar days, each carrying its digest row if one exists. */
const buildSpark = (rows, today) => {
    const byDate = new Map(rows.map((r) => [r.digestDate, r]));
    const out = [];
    for (let i = SPARK_DAYS - 1; i >= 0; i -= 1) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = toLocalDateKey(d);
        out.push({ key, date: d, row: byDate.get(key) || null });
    }
    return out;
};

const StatLine = ({ value, label, sub, tint }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', width: 64, color: tint || 'var(--cc-ink-900)' }}>{value}</div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>{label}</div>
            <div className="cc-tiny">{sub}</div>
        </div>
    </div>
);

const HistoryRow = ({ row, today, open, onToggle }) => {
    const date = parseDigestDate(row.digestDate);
    const rel = date ? relativeLabel(date, today) : null;
    const src = row.bySource || {};
    const status = row.byStatus || {};
    const total = (src.signal || 0) + (src.job || 0) + (src.contact || 0) || 1;
    const reviewed = wasActedOn(row);
    const sent = sentCount(row);
    const replies = row.counts?.replies || 0;

    const seg = (n, c, k) => (n ? <span key={k} style={{ width: `${(n / total) * 100}%`, background: c }} /> : null);

    return (
        <div className="cc-card" style={{ padding: 0, overflow: 'hidden', borderColor: reviewed ? 'var(--cc-border)' : '#FDE68A' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', flexWrap: 'wrap' }}>
                <div style={{ width: 104, flexShrink: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--cc-ink-900)' }}>
                        {date ? fmtDay(date) : row.digestDate}
                    </div>
                    {rel && <div className="cc-tiny" style={{ marginTop: 1 }}>{rel}</div>}
                </div>

                <div style={{ width: 150, flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {row.total} <span style={{ fontWeight: 500, color: 'var(--cc-ink-500)', fontSize: 12 }}>proposed</span>
                    </div>
                    <div style={{ marginTop: 6, height: 6, borderRadius: 9999, overflow: 'hidden', display: 'flex', background: '#F1F3F7' }}>
                        {[seg(src.signal, DD_TONE.signal.bar, 's'), seg(src.job, DD_TONE.job.bar, 'j'), seg(src.contact, DD_TONE.contact.bar, 'c')]}
                    </div>
                    <div className="cc-tiny" style={{ marginTop: 5 }}>
                        {[
                            src.signal ? `${src.signal} signals` : null,
                            src.job ? `${src.job} jobs` : null,
                            src.contact ? `${src.contact} contacts` : null,
                        ].filter(Boolean).join(' · ') || 'no breakdown'}
                    </div>
                </div>

                <div className="cc-grow" style={{ minWidth: 140 }}>
                    <div className="cc-trunc cc-body" style={{ color: 'var(--cc-ink-700)' }}>
                        {(row.sampleCompanies || []).join(' · ') || '—'}
                    </div>
                    {row.total > (row.sampleCompanies || []).length && (
                        <div className="cc-tiny" style={{ marginTop: 3 }}>
                            +{row.total - (row.sampleCompanies || []).length} more opportunities
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                    {sent > 0 && <span className="cc-chip cc-chip-primary"><Send style={{ width: 11, height: 11 }} />{sent} sent</span>}
                    {status.approved > 0 && <span className="cc-chip cc-chip-success"><Check style={{ width: 11, height: 11 }} />{status.approved} approved</span>}
                    {replies > 0 && <span className="cc-chip cc-chip-success"><MessageSquare style={{ width: 11, height: 11 }} />{replies} replied</span>}
                    {status.skipped > 0 && <span className="cc-chip cc-chip-neutral">{status.skipped} skipped</span>}
                    {status.failed > 0 && <span className="cc-chip cc-chip-danger">{status.failed} failed</span>}
                    {!reviewed && <span className="cc-chip cc-chip-warn">Not reviewed</span>}
                    <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" onClick={onToggle}>
                        {open ? 'Close' : 'Open'}
                        <ChevronRight style={{ width: 11, height: 11, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }} />
                    </button>
                </div>
            </div>

            {open && (
                <div style={{ borderTop: '1px solid var(--cc-divider)', background: '#FAFBFD', padding: '14px 18px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                    <div>
                        <span className="cc-chip cc-chip-primary">Acted on</span>
                        <div className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.6 }}>
                            {[
                                status.sent ? `${status.sent} executed` : null,
                                status.manual_sent ? `${status.manual_sent} marked sent by you` : null,
                                status.approved ? `${status.approved} approved, not yet executed` : null,
                            ].filter(Boolean).join('\n') || 'Nothing was acted on.'}
                        </div>
                    </div>
                    <div>
                        <span className="cc-chip cc-chip-neutral">Declined</span>
                        <div className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.6 }}>
                            {status.skipped ? `${status.skipped} skipped` : 'Nothing was skipped.'}
                            {status.failed ? `\n${status.failed} failed to execute` : ''}
                        </div>
                    </div>
                    <div>
                        <span className="cc-chip cc-chip-warn">Left open</span>
                        <div className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.6 }}>
                            {status.proposed
                                ? `${status.proposed} never reviewed${row.status === 'expired' ? ' — expired' : ''}`
                                : 'Every proposal got a decision.'}
                        </div>
                    </div>
                    <div>
                        <span className="cc-chip cc-chip-outline">Companies</span>
                        <div className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.6 }}>
                            {(row.sampleCompanies || []).join(', ') || '—'}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DigestHistory = ({ onGoToToday }) => {
    const dispatch = useDispatch();
    const { history, historyLoading } = useSelector((state) => state.digest);
    const [filter, setFilter] = useState('all');
    const [openId, setOpenId] = useState(null);

    useEffect(() => { dispatch(fetchDigestHistory()); }, [dispatch]);

    // Midnight today, in local time — the anchor every relative label and the
    // 30-day window are measured from.
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const rows = useMemo(() => history || [], [history]);
    const spark = useMemo(() => buildSpark(rows, today), [rows, today]);

    const stats = useMemo(() => {
        const proposed = rows.reduce((s, r) => s + (r.total || 0), 0);
        const sent = rows.reduce((s, r) => s + sentCount(r), 0);
        const replies = rows.reduce((s, r) => s + (r.counts?.replies || 0), 0);
        const notReviewed = rows.filter((r) => !wasActedOn(r)).length;
        const activeDays = rows.length;
        return {
            proposed,
            sent,
            replies,
            notReviewed,
            perDay: activeDays ? Math.round(proposed / activeDays) : 0,
            // A rate over zero sends is not 0% — it is undefined, and printing
            // "0% reply rate" would read as a performance failure that has not
            // happened yet.
            sentPct: proposed ? `${((sent / proposed) * 100).toFixed(1)}% of proposals` : 'no proposals yet',
            replyPct: sent ? `${Math.round((replies / sent) * 100)}% reply rate` : 'nothing sent yet',
            activeDays,
        };
    }, [rows]);

    const counts = useMemo(() => ({
        all: rows.length,
        acted: rows.filter(wasActedOn).length,
        missed: rows.filter((r) => !wasActedOn(r)).length,
    }), [rows]);

    const filtered = useMemo(() => {
        if (filter === 'acted') return rows.filter(wasActedOn);
        if (filter === 'missed') return rows.filter((r) => !wasActedOn(r));
        return rows;
    }, [rows, filter]);

    // "This week" is the last 7 days; everything older is grouped beneath it.
    const weekAgo = useMemo(() => new Date(today.getTime() - 6 * 86400000), [today]);
    const thisWeek = filtered.filter((r) => { const d = parseDigestDate(r.digestDate); return d && d >= weekAgo; });
    const earlier = filtered.filter((r) => { const d = parseDigestDate(r.digestDate); return !d || d < weekAgo; });

    /* Built from the rows already loaded — no endpoint required, and it exports
       exactly what the screen is showing rather than a different silent query. */
    const exportCsv = () => {
        const head = ['date', 'proposed', 'signals', 'jobs', 'contacts', 'approved', 'skipped', 'sent', 'failed', 'replies', 'status', 'companies'];
        const lines = rows.map((r) => {
            const s = r.bySource || {};
            const st = r.byStatus || {};
            return [
                r.digestDate, r.total || 0, s.signal || 0, s.job || 0, s.contact || 0,
                st.approved || 0, st.skipped || 0, sentCount(r), st.failed || 0,
                r.counts?.replies || 0, r.status || '',
                `"${(r.sampleCompanies || []).join('; ').replace(/"/g, '""')}"`,
            ].join(',');
        });
        const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `career-champ-digest-history-${toLocalDateKey(today)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (historyLoading && rows.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0', color: 'var(--cc-ink-400)' }}>
                <Loader2 size={22} className="animate-spin" />
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="cc-card" style={{ padding: '56px 24px', textAlign: 'center' }}>
                <p className="cc-h3">No past digests yet</p>
                <p className="cc-tiny" style={{ marginTop: 6 }}>
                    Once the agent has prepared a few days of outreach, every run and its outcome shows up here.
                </p>
            </div>
        );
    }

    const sparkMax = Math.max(1, ...spark.map((d) => d.row?.total || 0));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div className="cc-grow">
                    <div className="cc-h1">Digest history</div>
                    <div className="cc-tiny" style={{ marginTop: 4 }}>Every run the agent has prepared, and what came of it</div>
                </div>
                <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" onClick={exportCsv}>
                    <Download style={{ width: 12, height: 12 }} />Export CSV
                </button>
                <button type="button" className="cc-btn cc-btn-primary cc-btn-sm" onClick={onGoToToday}>
                    Go to today
                </button>
            </div>

            <div className="cc-card cc-history-summary" style={{ padding: '18px 20px' }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div className="cc-h3">Last {SPARK_DAYS} days</div>
                        <span style={{ flex: 1 }} />
                        {[['Signals', DD_TONE.signal.bar], ['Jobs', DD_TONE.job.bar], ['Contacts', DD_TONE.contact.bar]].map(([l, c]) => (
                            <span key={l} className="cc-tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 3, background: c }} />{l}
                            </span>
                        ))}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 104, marginTop: 16 }}>
                        {spark.map((d) => {
                            const row = d.row;
                            const t = row?.total || 0;
                            const s = row?.bySource || {};
                            const title = `${fmtDay(d.date)} — ${t ? `${t} proposed` : 'no digest'}`;
                            return (
                                <div key={d.key} className="cc-grow" title={title} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                                    {t === 0 ? (
                                        /* A day with no digest is a flat rule, not a zero-height
                                           bar — the gap is the information. */
                                        <div style={{ height: 4, borderRadius: 3, background: '#EDEFF4' }} />
                                    ) : (
                                        <div style={{ height: `${(t / sparkMax) * 100}%`, borderRadius: '4px 4px 3px 3px', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 6 }}>
                                            {(s.signal || 0) > 0 && <span style={{ flex: s.signal, background: DD_TONE.signal.bar }} />}
                                            {(s.job || 0) > 0 && <span style={{ flex: s.job, background: DD_TONE.job.bar }} />}
                                            {(s.contact || 0) > 0 && <span style={{ flex: s.contact, background: DD_TONE.contact.bar }} />}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', marginTop: 8 }}>
                        <span className="cc-tiny">{fmtDay(spark[0].date)}</span>
                        <span style={{ flex: 1 }} />
                        <span className="cc-tiny">{fmtDay(today)} · today</span>
                    </div>
                </div>

                <div style={{ borderLeft: '1px solid var(--cc-divider)', paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 14, justifyContent: 'center' }}>
                    <StatLine value={stats.proposed.toLocaleString()} label="Proposed" sub={`${stats.perDay} per active day`} />
                    <StatLine value={stats.sent} label="Sent" sub={stats.sentPct} />
                    <StatLine value={stats.replies} label="Replies" sub={stats.replyPct} tint={stats.replies ? '#047857' : undefined} />
                    <StatLine
                        value={stats.notReviewed}
                        label="Runs not reviewed"
                        sub={`of ${stats.activeDays} — proposals expired`}
                        tint={stats.notReviewed ? '#B45309' : undefined}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <DDSeg
                    active={filter}
                    onChange={setFilter}
                    items={[
                        { k: 'all', label: 'All runs', n: counts.all },
                        { k: 'acted', label: 'Acted on', n: counts.acted },
                        { k: 'missed', label: 'Not reviewed', n: counts.missed },
                    ]}
                />
                <span style={{ flex: 1 }} />
                <span className="cc-btn cc-btn-secondary cc-btn-sm" style={{ cursor: 'default' }}>
                    <Calendar style={{ width: 12, height: 12 }} />
                    {today.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </span>
            </div>

            {thisWeek.length > 0 && (
                <>
                    <div className="cc-eyebrow">This week</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {thisWeek.map((r) => (
                            <HistoryRow
                                key={r._id}
                                row={r}
                                today={today}
                                open={openId === r._id}
                                onToggle={() => setOpenId(openId === r._id ? null : r._id)}
                            />
                        ))}
                    </div>
                </>
            )}

            {earlier.length > 0 && (
                <>
                    <div className="cc-eyebrow" style={{ marginTop: 6 }}>Earlier</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {earlier.map((r) => (
                            <HistoryRow
                                key={r._id}
                                row={r}
                                today={today}
                                open={openId === r._id}
                                onToggle={() => setOpenId(openId === r._id ? null : r._id)}
                            />
                        ))}
                    </div>
                </>
            )}

            {filtered.length === 0 && (
                <div className="cc-card" style={{ padding: '40px 24px', textAlign: 'center' }}>
                    <p style={{ fontSize: 13, fontWeight: 700 }}>No runs match this filter</p>
                </div>
            )}
        </div>
    );
};

export default DigestHistory;
